import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate, Link } from "react-router-dom";
import { Search, Users, Bell, MessageSquare, Calendar, Eye } from "lucide-react";
import logo from "@/assets/privy-logo.png";
import { PageMeta } from "@/components/PageMeta";
import { ThemeToggle } from "@/components/ThemeToggle";

const rotatingWords = ["Dream Home", "Next Rental", "Next Flip", "Fixer Upper", "Investment"];

const Landing = () => {
  const navigate = useNavigate();
  const [wordIndex, setWordIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % rotatingWords.length);
        setAnimating(false);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Search className="h-8 w-8" />,
      title: "Advanced Search",
      description: "Search private listings with powerful filters and map views",
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Agent-Client Collaboration",
      description: "Private, ad-free workspace for agents and their buyers to work together",
    },
    {
      icon: <Bell className="h-8 w-8" />,
      title: "Instant Alerts",
      description: "Get notified immediately when new listings match your saved searches",
    },
    {
      icon: <MessageSquare className="h-8 w-8" />,
      title: "In-App Chat",
      description: "Discuss listings and coordinate with your agent without leaving the platform",
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: "Tour Scheduling",
      description: "Request and manage property tours with integrated scheduling",
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Activity Insights",
      description: "Agents see all client activity, preferences, and engagement in real-time",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Privy Listing Network | Private Home Search"
        description="A private real estate network where agents and buyers search off-market listings, save homes, chat, and schedule showings in one shared workspace."
        path="/"
      />
      <nav className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img src={logo} alt="Privy Listing Network" className="h-14 w-auto" />
          <div className="flex gap-2 items-center">
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate("/auth")}>Login</Button>
            <Button onClick={() => navigate("/auth")}>Get Started</Button>
          </div>
        </div>
      </nav>

      <main>
      <section className="container mx-auto px-4 py-12 md:py-20 text-center">
        <h1 className="text-3xl md:text-5xl font-bold mb-6">
          Find Your{" "}
          <span
            className="inline-block text-primary"
            style={{
              transform: animating ? "translateX(40px)" : "translateX(0)",
              opacity: animating ? 0 : 1,
              transition: "transform 0.4s ease-in-out, opacity 0.3s ease-in-out",
            }}
          >
            {rotatingWords[wordIndex]}
          </span>
          <span className="block mt-3 text-lg md:text-2xl font-medium text-muted-foreground">
            Privy Listing Network — collaborative real estate search for agents and buyers
          </span>
        </h1>


        <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          A collaborative real estate search platform connecting agents and buyers in an ad-free, streamlined
          experience
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/auth")}>
            Sign Up Free
          </Button>
          <Button size="lg" variant="outline" className="w-full sm:w-auto" onClick={() => navigate("/auth")}>
            Agent Login
          </Button>
        </div>
      </section>

      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12">Why Choose Privy Listing Network?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="text-primary mb-2">{feature.icon}</div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20">
        <h3 className="text-3xl font-bold text-center mb-12">How It Works</h3>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
              1
            </div>
            <h4 className="text-xl font-semibold mb-2">Agent Invites You</h4>
            <p className="text-muted-foreground">
              Your real estate agent creates an account and sends you a personalized invitation
            </p>
          </div>
          <div className="text-center">
            <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
              2
            </div>
            <h4 className="text-xl font-semibold mb-2">Search & Save</h4>
            <p className="text-muted-foreground">
              Browse listings, create saved searches, and get instant alerts for new matches
            </p>
          </div>
          <div className="text-center">
            <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
              3
            </div>
            <h4 className="text-xl font-semibold mb-2">Collaborate & Tour</h4>
            <p className="text-muted-foreground">
              Chat with your agent, request tours, and work together to find your perfect home
            </p>
          </div>
        </div>
      </section>
      </main>

      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Privy Listing Network. All rights reserved.</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link to="/private-real-estate-listings" className="hover:text-foreground transition-colors">
              Private Real Estate Listings
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
